import { Injectable } from '@angular/core';

import { ApiEmplifiPhoto, ApiEmplifiProduct, ApiEmplifiSearch, ApiHudsonSearchContent } from './api-hudson.models';

/**
 * Service responsible for transforming Emplifi API responses to DashHudson format.
 * This ensures consistency across all components using this transformation.
 */
@Injectable()
export class EmplifiHudsonTransformerService {

  /**
   * Transforms an Emplifi search response to DashHudson format for compatibility.
   * Centralizing this logic prevents drift between render and workspace components.
   *
   * @param response - The Emplifi API response
   * @returns An array of ApiHudsonSearchContent items in DashHudson format
   */
  public transformEmplifiToHudsonFormat(response: ApiEmplifiSearch): ApiHudsonSearchContent[] {
    /* eslint-disable @typescript-eslint/naming-convention */
    return response.data?.map((photo: ApiEmplifiPhoto, index: number) => {
      const createdAt = photo.approved_at ?? photo.submitted_at ?? new Date().toISOString();
      const { originalUrl, fullUrl, ratioUrl, squareUrl } = this.getImageUrls(photo);
      const mappedProducts = (photo.products ?? []).map((product: ApiEmplifiProduct) => {
        const productId = product.sku;
        return {
          id: productId,
          source_id: productId,
          product_groups: [],
          url: product.link
        };
      }) as unknown as ApiHudsonSearchContent['products'];

      return {
        id: photo.id.toString(),
        brand_media_type: photo.source,
        clicks: 0,
        created_at: createdAt,
        user_name: photo.user_name,
        media: {
          id: photo.album_photo_id,
          created_at: createdAt,
          image_sizes: {
            medium_square: {
              url: squareUrl,
              height: 300,
              width: 300
            },
            original: {
              url: originalUrl,
              height: 0,
              width: 0
            },
            original_converted: {
              url: fullUrl,
              height: 0,
              width: 0
            },
            small: {
              url: ratioUrl,
              height: 300,
              width: 300
            },
            small_square: {
              url: squareUrl,
              height: 300,
              width: 300
            }
          },
          media_group: photo.album_id,
          original_height: 0,
          original_width: 0,
          predictions: {
            eng: 0
          },
          source: photo.platform?.toUpperCase() ?? 'EMPLIFI',
          source_created_at: createdAt,
          source_id: photo.native_id,
          type: photo.content_type?.toUpperCase() ?? 'IMAGE',
          updated_at: createdAt,
          urls: {
            640: squareUrl,
            full: fullUrl,
            original: originalUrl,
            ratio: ratioUrl,
            thumbs: squareUrl
          },
          video_sizes: {}
        },
        order: index,
        products: mappedProducts,
        updated_at: createdAt
      };
    }) ?? [];
  }

  private getImageUrls(photo: ApiEmplifiPhoto): {
    originalUrl: string;
    fullUrl: string;
    ratioUrl: string;
    squareUrl: string;
  } {
    const originalUrl = photo.pixlee_cdn_photos?.original_url
      || photo.source_url;
    const fullUrl = photo.pixlee_cdn_photos?.large_url
      || photo.big_url
      || originalUrl;
    const ratioUrl = photo.pixlee_cdn_photos?.medium_url
      || photo.medium_url
      || fullUrl;
    const squareUrl = photo.pixlee_cdn_photos?.square_medium_url
      || photo.square_medium_url
      || ratioUrl;

    return {
      originalUrl,
      fullUrl,
      ratioUrl,
      squareUrl
    };
  }

}
